import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiGift, FiMail, FiTrash2 } from 'react-icons/fi';
import { deleteCommercialGesture, getCommercialGesture, sendCommercialGestureRibLink } from '../../services/api';
import {
    canSendCgRibLink,
    formatCgAmount,
    formatCgDate,
    getCgErrorToast,
    parseCgError,
    unwrapCgData,
} from './commercialGestures/commercialGestureUtils';
import {
    CgDeleteConfirmModal,
    CgSendRibLinkModal,
    CgPolicyPreview,
    CgRibLink,
    CgToastStack,
    CommercialGestureStatusBadge,
    useCgToasts,
    useCommercialGesturePermissions,
} from './commercialGestures/CommercialGestureShared';
import './commercialGestures/commercialGestures.css';

const AdminCommercialGestureDetail = () => {
    const { gestureId } = useParams();
    const navigate = useNavigate();
    const { canRead, canWrite } = useCommercialGesturePermissions();
    const { toasts, push, dismiss } = useCgToasts();
    const [gesture, setGesture] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDelete, setShowDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showSend, setShowSend] = useState(false);
    const [sending, setSending] = useState(false);
    const sendInFlightRef = useRef(false);

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!canRead || !gestureId) return;
        if (!silent) setLoading(true);
        setError(null);
        try {
            const data = unwrapCgData(await getCommercialGesture(gestureId));
            setGesture(data);
        } catch (err) {
            setError(getCgErrorToast(parseCgError(err)));
        } finally {
            if (!silent) setLoading(false);
        }
    }, [canRead, gestureId]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteCommercialGesture(gestureId);
            navigate('/admin/trips/commercial-gestures', {
                state: { toast: { message: 'Le geste commercial a été supprimé.', variant: 'success' } },
            });
        } catch (err) {
            push(getCgErrorToast(parseCgError(err)), 'danger');
            setDeleting(false);
        }
    };

    const handleSendRibLink = async () => {
        if (sendInFlightRef.current || !gestureId) return;
        sendInFlightRef.current = true;
        setSending(true);
        try {
            await sendCommercialGestureRibLink(gestureId);
            const resent = gesture?.status === 'send';
            setShowSend(false);
            push(resent ? 'Le lien RIB a été renvoyé.' : 'Le lien RIB a été envoyé.');
            await load({ silent: true });
        } catch (err) {
            push(getCgErrorToast(parseCgError(err)), 'danger');
        } finally {
            sendInFlightRef.current = false;
            setSending(false);
        }
    };

    if (!canRead) {
        return (
            <div className="dashboard-page">
                <Alert variant="danger">Action non autorisée</Alert>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <CgToastStack toasts={toasts} onDismiss={dismiss} />
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiGift className="me-2" aria-hidden="true" />
                                Geste commercial
                            </h2>
                        </div>
                        <div className="page-header-actions cg-actions">
                            {canWrite && gesture && (
                                <>
                                    <Button
                                        variant="outline-success"
                                        onClick={() => setShowSend(true)}
                                        disabled={!canSendCgRibLink(gesture.status) || sending}
                                        title={
                                            gesture.status === 'send'
                                                ? 'Le lien RIB a déjà été envoyé pour ce geste commercial.'
                                                : undefined
                                        }
                                    >
                                        {sending ? (
                                            <Spinner size="sm" className="me-2" animation="border" />
                                        ) : (
                                            <FiMail className="me-2" />
                                        )}
                                        Envoyer le lien RIB
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => navigate(`/admin/trips/commercial-gestures/${gesture.id}/edit`)}
                                    >
                                        <FiEdit className="me-2" />
                                        Modifier
                                    </Button>
                                    <Button variant="outline-danger" onClick={() => setShowDelete(true)}>
                                        <FiTrash2 className="me-2" />
                                        Supprimer
                                    </Button>
                                </>
                            )}
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/trips/commercial-gestures')}>
                                <FiArrowLeft className="me-2" />
                                Retour
                            </Button>
                        </div>
                    </div>

                    {loading && (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    )}
                    {error && !loading && <Alert variant="danger" className="mt-3">{error}</Alert>}
                    {!loading && !error && !gesture && (
                        <Alert variant="info" className="mt-3">Geste commercial introuvable.</Alert>
                    )}
                    {gesture?.status === 'send' && (
                        <Alert variant="warning" className="mt-3">
                            Le lien RIB a déjà été envoyé pour ce geste commercial.
                        </Alert>
                    )}

                    {gesture && (
                        <div className="mt-4">
                            <CgPolicyPreview policy={gesture} />
                            <dl className="cg-readonly-grid">
                                <div>
                                    <dt>Montant</dt>
                                    <dd>{formatCgAmount(gesture.amount)}</dd>
                                </div>
                                <div>
                                    <dt>Statut</dt>
                                    <dd><CommercialGestureStatusBadge status={gesture.status} /></dd>
                                </div>
                                <div>
                                    <dt>Créé le</dt>
                                    <dd>{formatCgDate(gesture.created_at)}</dd>
                                </div>
                                <div>
                                    <dt>Modifié le</dt>
                                    <dd>{formatCgDate(gesture.updated_at)}</dd>
                                </div>
                                {gesture.policy_id && (
                                    <div>
                                        <dt>Police</dt>
                                        <dd>
                                            <Link to={`/admin/trips/policies/${gesture.policy_id}`}>
                                                {gesture.policy_code || 'Ouvrir la police'}
                                            </Link>
                                        </dd>
                                    </div>
                                )}
                            </dl>
                            <CgRibLink
                                link={gesture.rib_link}
                                onCopied={() => push('Lien RIB copié.')}
                                onCopyError={() => push('Impossible de copier le lien RIB.', 'danger')}
                            />
                        </div>
                    )}
                </Card.Body>
            </Card>

            <CgSendRibLinkModal
                show={showSend}
                gesture={gesture}
                sending={sending}
                onCancel={() => !sending && setShowSend(false)}
                onConfirm={handleSendRibLink}
            />
            <CgDeleteConfirmModal
                show={showDelete}
                gesture={gesture}
                deleting={deleting}
                onCancel={() => setShowDelete(false)}
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default AdminCommercialGestureDetail;
